import {
  Card,
  CardContent,
  Divider,
  Stack,
  styled,
  Switch,
  Tooltip,
  Typography,
  type Theme,
} from '@mui/material';
import type { Condition, BackupPolicyConfig } from '@repo/api-gw-sdk';
import {
  BackupPolicyType,
  BackupStatus,
  ConditionOperatorEnum,
  Provider,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/accordion';
import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import { Loader } from '@/components/layout/loading';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { backupPolicies } from '@/components/queryBuilder/properties';
import {
  getResourcesScopeType,
  type ResourcesScopeType,
} from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { NavigationLink } from '@/components/shared/link';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import type { BackupVault } from '@/data/vaults/backupVault';
import { createCombineCondition } from '@/types/advanceFilter';

import { BackupPolicyConditionTitle } from './BackupPolicyConditionEditor';
import {
  isContinuousPolicyType,
  isNativePolicyType,
  isPitrPolicyType,
} from './backupPoliciesTypes';
import { useAllBackupPolicyProperties } from './backupPolicyProperties';
import {
  BackupPolicyContinuousSchedulesTable,
  BackupPolicySchedulesTable,
} from './backupPolicySchedulesTable';
import { BackupPolicyTypeTag } from './backupPolicyTypeTag';
import { BackupPolicyTypeDescription } from './customPolicySupportedDisclaimer';

export const BPCard = styled(Card)((props: {
  disabled: boolean;
  theme?: Theme;
  style?: object;
}) => {
  return {
    border: '1px solid var(--mui-palette-divider)',
    backgroundColor: props.disabled
      ? 'var(--mui-palette-background-default)'
      : 'var(--mui-palette-background-paper)',
    ...props.theme?.typography.body2,
    textAlign: 'left',
    color: props.theme?.palette.text.secondary,
    minWidth: '550px',
    minHeight: '170px',
    boxShadow: 'none',
    paddingTop: '12px',
    borderRadius: '16px',
    '& .MuiCardContent-root': {
      padding: '0px 24px 24px',
    },
    ...props.style,
  };
});

interface BackupPolicyCardProps {
  processing?: boolean;
  policy: BackupPolicyConfig;
  vaults?: BackupVault[];
  defaultExpanded?: boolean;
  onEdit?: (policy: BackupPolicyConfig) => void;
  onDelete?: (policy: BackupPolicyConfig) => void;
  onDuplicate?: (policy: BackupPolicyConfig) => void;
  onEnabledPolicyUpdate?: (policy: BackupPolicyConfig) => void;
}

// backupPolicyNativeProvider resolves the cloud provider used by the
// AccountAsVaultTag shown on native policy cards. For AZURE_FILES we pin
// to Azure directly (no resourceTypes carried on the policy). For the
// other native types we infer from the first resourceType, falling back
// to AWS for backward compatibility with older policies.
const backupPolicyNativeProvider = (policy: BackupPolicyConfig): Provider => {
  if (policy.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES) {
    return Provider.Azure;
  }
  const first = policy.resourceTypes?.[0];
  return first ? ResourceTypes[first].provider : Provider.Aws;
};

export function BackupPolicyCard(props: BackupPolicyCardProps) {
  const enabled = props.policy.enabled;
  const { snackbar } = useWorkspace();
  const { isAuthorized } = useRoles();
  const allBackupPolicyProperties = useAllBackupPolicyProperties();
  const [vaultsSectionExpanded, setVaultsSectionExpanded] = useState(
    !!props.defaultExpanded
  );
  const [disablePolicyDialogOpen, setDisablePolicyDialogOpen] = useState(false);
  const matchingVaults = props.vaults
    ?.filter((vault) =>
      props.policy?.schedules.find((schedule) => schedule.vaultId === vault.id)
    )
    .slice(0, 2);
  const resourcesScopeType: ResourcesScopeType = getResourcesScopeType(
    props.policy.condition,
    'allIncluded'
  );

  const togglePolicyEnabledState = () => {
    const enabled = props.policy.enabled;
    props.onEnabledPolicyUpdate?.(props.policy);
    snackbar.showMessage(
      <>
        <span className='font-semibold'>{props.policy.name}</span> backup policy
        is now {enabled ? 'disabled' : 'enabled'}.
      </>
    );
  };

  const onToggleClicked = () => {
    if (enabled) {
      setDisablePolicyDialogOpen(true);
    } else {
      togglePolicyEnabledState();
    }
  };

  const inventoryFilterCondition: Condition = {
    operator: ConditionOperatorEnum.ContainsAnyOf,
    value: [props.policy?.id || ''],
    property: backupPolicies.name,
  };

  return (
    <BPCard disabled={!enabled} data-testid='backup-policy-card'>
      <>
        <Stack
          direction='row'
          sx={{
            maxWidth: '100%',
            justifyContent: 'space-between',
            gap: '12px',
            alignItems: 'center',
            margin: '12px 24px',
          }}
        >
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              gap: '12px',
              minWidth: '0px',
              flexShrink: 1,
            }}
          >
            <Stack
              sx={{
                minWidth: '0px',
                flexShrink: 1,
              }}
            >
              <OverflowTooltip
                text={props.policy.name}
                renderContent={(text, ref) => (
                  <Typography noWrap variant='h3' ref={ref}>
                    {text}
                  </Typography>
                )}
              />
            </Stack>
            <BackupPolicyTypeTag policyType={props.policy.policyType} />
          </Stack>
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              gap: '16px',
            }}
          >
            {enabled && (
              <>
                {props.processing ? (
                  <Loader variant='inline' />
                ) : (
                  <NavigationLink
                    path='/inventory'
                    searchParams={{
                      query: createCombineCondition([
                        inventoryFilterCondition,
                        createBackupStatusNotInCondition([
                          BackupStatus.ExcludedFromBackup,
                          BackupStatus.Disconnected,
                          BackupStatus.Unsupported,
                          BackupStatus.LimitExceeded,
                        ]),
                      ]),
                    }}
                    disabled={!isAuthorized('read:inventory')} // fix this to check read:backup-policies once directing to backupPolicyPage
                    className='text-(--mui-palette-primary-main)'
                  >
                    <ButtonWithIcon
                      text={`${props.policy.attachedResources ?? 0} Resources`}
                      iconPosition='right'
                      icon='material-symbols-arrow-right-alt'
                    />
                  </NavigationLink>
                )}
                {((props.onEdit && props.onDelete && props.onDuplicate) ||
                  props.onEnabledPolicyUpdate) && (
                  <Divider sx={{ height: '16px' }} orientation='vertical' />
                )}
              </>
            )}

            {props.onEdit && props.onDelete && props.onDuplicate && (
              <OptionMenu
                options={[
                  {
                    id: 'edit-policy',
                    text: 'Edit',
                    menuItemProps: {
                      disabled: !isAuthorized('update:policies'),
                      onClick: () => {
                        if (!props.policy) {
                          return;
                        }
                        props.onEdit?.(props.policy);
                      },
                    },
                  },
                  {
                    id: 'duplicate-policy',
                    text: 'Duplicate',
                    menuItemProps: {
                      disabled: !isAuthorized('create:policies'),
                      onClick: () => {
                        props.onDuplicate?.(props.policy);
                      },
                    },
                  },
                  {
                    id: 'divider',
                    divider: true,
                  },
                  {
                    id: 'delete-policy',
                    text: 'Delete',
                    menuItemProps: {
                      disabled: !isAuthorized('delete:policies'),
                      onClick: () => {
                        if (!props.policy) {
                          return;
                        }
                        props.onDelete?.(props.policy);
                      },
                    },
                  },
                ]}
              />
            )}
            {props.onEnabledPolicyUpdate && (
              <Tooltip
                title={
                  enabled ? 'This policy is enabled' : 'This policy is disabled'
                }
              >
                <Switch
                  disabled={!isAuthorized('update:policies')}
                  checked={enabled}
                  onChange={onToggleClicked}
                />
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <CardContent sx={{ marginTop: '24px' }}>
          <Accordion defaultExpanded={props.defaultExpanded}>
            <AccordionSummary>
              <Typography variant='h4'>{'Resource selection'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack
                sx={{
                  gap: '24px',
                }}
              >
                <ResourcesSelectionViewer
                  condition={props.policy.condition}
                  conditionTitle={
                    <BackupPolicyConditionTitle
                      resourceTypes={props.policy.resourceTypes}
                      policyType={
                        props.policy.policyType ??
                        BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD
                      }
                    />
                  }
                  include={props.policy.include ?? []}
                  exclude={props.policy.exclude ?? []}
                  resourcesScopeType={resourcesScopeType}
                  dynamicResourcesHeader={null}
                  allExcludedDescription={
                    'This backup policy won’t be automatically applied to any resources. Only manually included resources will be assigned to the policy.'
                  }
                  allIncludedDescription={
                    isPitrPolicyType(props.policy.policyType)
                      ? 'This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they’ll be included too.'
                      : 'This backup policy will be automatically applied to all resources. When new ones are discovered, they’ll be included too.'
                  }
                  supportProperties={allBackupPolicyProperties.flat()}
                  selectedResourceTypes={props.policy.resourceTypes}
                  policyType={props.policy.policyType}
                />
                <BackupPolicyTypeDescription
                  policyType={props.policy.policyType}
                  showForPitr={false}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
          <Divider sx={{ margin: '12px -24px' }} />
          <Accordion
            expanded={vaultsSectionExpanded}
            onChange={() => setVaultsSectionExpanded(!vaultsSectionExpanded)}
          >
            <AccordionSummary>
              <Stack
                direction='row'
                className='w-full'
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant='h4'>Backup schedules</Typography>
                {!vaultsSectionExpanded && (
                  <div>
                    {isNativePolicyType(props.policy.policyType) ? (
                      <AccountAsVaultTag
                        cloudProvider={backupPolicyNativeProvider(props.policy)}
                        region={
                          props.policy.schedules?.[0]?.region || 'Source region'
                        }
                      />
                    ) : (
                      !!matchingVaults?.length &&
                      matchingVaults.map((vault) => (
                        <VaultTag key={vault.id} vault={vault} />
                      ))
                    )}
                  </div>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingRight: 0, paddingTop: '24px' }}>
              {isContinuousPolicyType(props.policy.policyType) ? (
                <BackupPolicyContinuousSchedulesTable policy={props.policy} />
              ) : (
                <BackupPolicySchedulesTable
                  policy={props.policy}
                  vaults={props.vaults}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </CardContent>
        <ConfirmationDialog
          isOpen={disablePolicyDialogOpen}
          title='Disable backup policy'
          description={
            <>
              The <span className='font-semibold'>{props.policy.name}</span>{' '}
              backup policy will stop being applied to resources. Existing
              snapshots will be kept until their retention expires.
            </>
          }
          onCancel={() => setDisablePolicyDialogOpen(false)}
          onConfirm={() => {
            togglePolicyEnabledState();
            setDisablePolicyDialogOpen(false);
          }}
          buttonTitle='Disable Policy'
        />
      </>
    </BPCard>
  );
}
